import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  CriterionCardChrome,
  CriterionDefinitionField,
  CriterionLabelField,
  CriterionStableKeyField,
  type CriterionCardChromeProps,
} from '@/components/admin/evaluation-rubrics/criterion-card-chrome'
import type { RubricCriterion } from '@/hooks/use-evaluation-rubrics-admin'
import { slugifyCriterionKey } from '@/lib/rubric-publish-payload'

type RubricDimensionCardProps = Omit<CriterionCardChromeProps, 'children' | 'footer'> & {
  onUpdate: (patch: Partial<RubricCriterion>) => void
  /** full = L1+advanced; advanced = stable key only (canvas L2). */
  depth?: 'full' | 'advanced'
}

export function RubricDimensionCard({
  row,
  errors,
  onUpdate,
  depth = 'full',
  ...chromeProps
}: RubricDimensionCardProps) {
  const [advancedOpen, setAdvancedOpen] = useState(depth === 'advanced')

  if (depth === 'advanced') {
    return (
      <CriterionCardChrome row={row} errors={errors} {...chromeProps}>
        <p className="text-sm font-medium">{row.label || row.key}</p>
        {row.definition ? (
          <p className="text-sm text-muted-foreground">{row.definition}</p>
        ) : null}
        <CriterionStableKeyField row={row} error={errors?.key} onUpdate={onUpdate} />
      </CriterionCardChrome>
    )
  }

  return (
    <CriterionCardChrome row={row} errors={errors} {...chromeProps}>
      <CriterionLabelField
        row={row}
        error={errors?.label}
        onUpdate={(patch) => {
          onUpdate(patch)
          if (patch.label?.trim()) {
            const autoKey = slugifyCriterionKey(patch.label, 'dimension')
            if (
              !row.key
              || row.key.startsWith('dim_')
              || row.key.startsWith('criterion_')
            ) {
              onUpdate({ ...patch, key: autoKey })
            }
          }
        }}
      />
      <CriterionDefinitionField row={row} onUpdate={onUpdate} />
      <div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto px-0"
          onClick={() => setAdvancedOpen((o) => !o)}
        >
          {advancedOpen ? (
            <ChevronDown className="mr-1 h-4 w-4" aria-hidden />
          ) : (
            <ChevronRight className="mr-1 h-4 w-4" aria-hidden />
          )}
          Advanced
        </Button>
        {advancedOpen ? (
          <div className="pt-2">
            <CriterionStableKeyField row={row} error={errors?.key} onUpdate={onUpdate} />
          </div>
        ) : null}
      </div>
    </CriterionCardChrome>
  )
}
